/* Single error class for everything thrown by the API layer.
 * Screens should `instanceof ApiError` to branch on `code`/`status`. */

export class ApiError extends Error {
  constructor({ code, message, status, details, traceId, retryable }) {
    super(message || code || 'request failed');
    this.name = 'ApiError';
    this.code = code || 'unknown';
    this.status = status ?? 0;
    this.details = details ?? null;
    this.traceId = traceId ?? null;
    this.retryable = !!retryable;
  }

  static network(cause) {
    return new ApiError({ code: 'network', message: 'Network unavailable', status: 0, details: cause?.message, retryable: true });
  }

  static timeout() {
    return new ApiError({ code: 'timeout', message: 'Request timed out', status: 0, retryable: true });
  }

  static fromResponse(status, payload, traceId) {
    return new ApiError({
      code: payload?.code || `http_${status}`,
      message: payload?.message || `Request failed (${status})`,
      status,
      details: payload?.details ?? payload,
      traceId,
      retryable: status >= 500 || status === 429,
    });
  }

  get isAuth() { return this.status === 401; }
  get isForbidden() { return this.status === 403; }
  get isNotFound() { return this.status === 404; }
  get isConflict() { return this.status === 409; }   // e.g. out-of-stock, slot taken
  get isValidation() { return this.status === 422; }
}

/** UX-friendly message a screen can show without case-by-casing every code. */
export function humanizeError(err) {
  if (!(err instanceof ApiError)) return err?.message || 'Something went wrong';
  if (err.code === 'network') return "You're offline. Check your connection and retry.";
  if (err.code === 'timeout') return 'That took too long — please retry.';
  if (err.isAuth) return 'Your session expired. Please sign in again.';
  if (err.isForbidden) return 'You do not have access to that.';
  if (err.isNotFound) return 'We could not find that.';
  if (err.isConflict) return err.message || 'That item is no longer available.';
  if (err.isValidation) return err.message || 'Please check the highlighted fields.';
  return err.message || 'Something went wrong.';
}
